import {
  Box,
  Button,
  ChakraStyledOptions,
  Image,
  useToast,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AccountLeadStore } from "store/UserAccounts/UserAccount/AccountLeads/AccountLead.store";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_WARNING_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { ApiMessageStack, Tooltip } from "components";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import {
  IntegrationLogoIcons,
  IntegrationLogoIconURLs,
} from "constants/integrationLogoIcons";

interface IProps {
  leadStore: AccountLeadStore;
  accountStore: UserAccountStore;
  hasActiveMRIRadarIntegration: boolean;
  styles?: ChakraStyledOptions;
}

export const LeadSyncMRIRadarButton: FC<IProps> = observer(
  ({ leadStore, accountStore, hasActiveMRIRadarIntegration, styles }) => {
    const toast = useToast();
    const [isSyncing, setIsSyncing] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(
      ApiRequestStatusEnum.NONE
    );

    const isLeadSynced = leadStore.isLeadSyncedToMRIRadar;
    const isLeadAssignedToUser = leadStore.isLeadAssignedToUser;
    const isLeadAssignedUserMappedToMRIRadar =
      leadStore.isLeadAssignedUserMappedToMRIRadar;

    const accountUsersStore = accountStore.accountUsersStore;
    const accountIntegrationMRIRadarNegotiatorsStore =
      accountStore.accountIntegrationsStore.accountIntegrationMRIRadarStore
        .accountIntegrationMRIRadarNegotiatorsStore;

    useEffect(() => {
      let isActive = true;

      const fetchData = async () => {
        try {
          setLoadingStatus(ApiRequestStatusEnum.PENDING);
          await Promise.all([
            accountUsersStore.fetchAccountUsers(),
            accountIntegrationMRIRadarNegotiatorsStore.fetchAccountMRIRadarNegotiatorsRelations(),
          ]);
          if (isActive) setLoadingStatus(ApiRequestStatusEnum.SUCCESS);
        } catch (error) {
          if (!isActive) return;
          setLoadingStatus(ApiRequestStatusEnum.ERROR);
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [accountUsersStore, accountIntegrationMRIRadarNegotiatorsStore, toast]);

    const isLoadingValidationData =
      loadingStatus === ApiRequestStatusEnum.NONE ||
      loadingStatus === ApiRequestStatusEnum.PENDING;

    const validationErrorMessages = useMemo(() => {
      const errorMessages: string[] = [];

      if (isSyncing) errorMessages.push("Lead is being synced...");
      if (isLeadSynced) errorMessages.push("Lead already synced.");
      if (!isLeadAssignedToUser) {
        errorMessages.push("Please assign a user.");
      } else if (!isLeadAssignedUserMappedToMRIRadar) {
        errorMessages.push("Assigned user is not mapped to MRI Radar.");
      }

      return errorMessages;
    }, [
      isSyncing,
      isLeadSynced,
      isLeadAssignedToUser,
      isLeadAssignedUserMappedToMRIRadar,
    ]);

    const buttonData = useMemo(() => {
      return {
        buttonProps: {
          isDisabled:
            isLoadingValidationData || !!validationErrorMessages.length,
          isLoading: isSyncing || isLoadingValidationData,
          children: isLeadSynced ? "Synced to Radar" : "Sync to Radar",
          leftIcon: isLeadSynced ? (
            <FontAwesomeIcon icon={faCircleCheck} fontSize={24} />
          ) : (
            <Image
              alt={"mri-radar-icon"}
              width={"24px"}
              src={IntegrationLogoIconURLs[IntegrationLogoIcons.MRI]}
            />
          ),
        },
      };
    }, [
      isLeadSynced,
      isSyncing,
      isLoadingValidationData,
      validationErrorMessages.length,
    ]);

    const handleSyncLead = useCallback(async () => {
      if (hasActiveMRIRadarIntegration && !validationErrorMessages.length) {
        try {
          setIsSyncing(true);
          await leadStore.fetchDetails();
          if (leadStore.isLeadSyncedToMRIRadar) {
            toast({
              ...DEFAULT_WARNING_TOAST_OPTIONS,
              description:
                "This lead has already been sync'd to MRI Radar by another user.",
            });
            return;
          }
          await leadStore.syncLeadWithMRIRadar();
        } catch (e) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={e.message} />,
          });
        } finally {
          setIsSyncing(false);
        }
      }
    }, [
      leadStore,
      hasActiveMRIRadarIntegration,
      validationErrorMessages,
      toast,
    ]);

    return (
      <Tooltip
        isDisabled={
          !validationErrorMessages.length ||
          loadingStatus !== ApiRequestStatusEnum.SUCCESS
        }
        aria-label={`lead-mri-radar-sync-tooltip`}
        label={
          <Box>
            {validationErrorMessages.map(message => (
              <Box key={message}>{message}</Box>
            ))}
          </Box>
        }
      >
        <Box>
          <Button
            onClick={handleSyncLead}
            variant={"ghost"}
            {...buttonData.buttonProps}
            {...styles}
          />
        </Box>
      </Tooltip>
    );
  }
);
